import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import apiService from '../services/api';
import './ShoppingCart.css';

const ShoppingCart = () => {
  const { t, language } = useLanguage();
  const {
    isCartOpen,
    cartItems,
    updateQuantity,
    removeFromCart,
    closeCart
  } = useCart();
  
  const navigate = useNavigate();
  const numberLocale = language === 'hi' ? 'hi-IN' : language === 'or' ? 'or-IN' : 'en-IN';

  const formatPrice = (price) => {
    return new Intl.NumberFormat(numberLocale, {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(price);
  };

  const toTranslationKey = (value = '') => value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const getTranslatedProductName = (name) => (
    t(`bhaktiStore.products.${toTranslationKey(name)}.name`, {}, name)
  );
  
  const cartTotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    try {
      const response = await apiService.getAllProducts();
      const latestProducts = Array.isArray(response?.data) ? response.data : [];
      const latestProductById = new Map(latestProducts.map(product => [product._id, product]));

      const unavailableItems = cartItems.filter((item) => {
        const latestProduct = latestProductById.get(item._id);
        if (!latestProduct) {
          return true;
        }
        const latestStock = Number(latestProduct?.stock);
        return Number.isFinite(latestStock) && latestStock <= 0;
      });

      if (unavailableItems.length > 0) {
        unavailableItems.forEach((item) => removeFromCart(item));
        const removedItemNames = unavailableItems
          .map((item) => getTranslatedProductName(item.name))
          .join(', ');
        window.alert(t('shoppingCart.outOfStockRemovedMessage', { items: removedItemNames }));
        return;
      }
    } catch (error) {
      // Preserve existing checkout behavior if stock validation API fails.
      console.error('Checkout stock validation failed:', error);
    }

    closeCart(); // Close the cart sidebar
    navigate('/checkout'); // Navigate to the dedicated checkout page
  };

  const handleIncreaseQuantity = (item, stockLimit) => {
    if (Number.isFinite(stockLimit) && item.quantity >= stockLimit) {
      window.alert(t('shoppingCart.quantityExceedsStockMessage'));
      return;
    }
    updateQuantity(item, item.quantity + 1);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            className="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.15 } }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            onClick={closeCart}
          />
          <motion.div
            className="shopping-cart"
            initial={{ x: '100%' }}
            animate={{ x: 0, transition: { duration: 0.15, ease: 'easeOut' } }}
            exit={{ x: '100%', transition: { duration: 0.1, ease: 'easeIn' } }}
          >
            <div className="cart-header">
              <h2>{t('shoppingCart.title')}</h2>
              <button className="close-btn" onClick={closeCart} aria-label={t('shoppingCart.closeCart')} title={t('shoppingCart.closeCart')}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="cart-content">
              {cartItems.length === 0 ? (
                <div className="empty-cart">
                  <i className="fas fa-shopping-bag"></i>
                  <h3>{t('shoppingCart.emptyTitle')}</h3>
                  <p>{t('shoppingCart.emptyDescription')}</p>
                </div>
              ) : (
                <>
                  <div className="cart-items">
                    {cartItems.map((item) => {
                      const stock = Number(item?.stock);
                      const stockLimit = Number.isFinite(stock) ? Math.max(0, Math.floor(stock)) : Number.POSITIVE_INFINITY;

                      return (
                        <motion.div
                          key={item._id}
                          className="cart-item"
                          layout
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="item-image">
                            {/* Use the first image from the images array */}
                            <img src={item.images[0]} alt={getTranslatedProductName(item.name)} />
                          </div>
                          <div className="item-details">
                            <h4>{getTranslatedProductName(item.name)}</h4>
                            <p className="item-price">{formatPrice(item.price)}</p>
                          </div>
                          <div className="item-controls">
                            <div className="quantity-controls">
                              <button
                                onClick={() => updateQuantity(item, item.quantity - 1)}
                                className="quantity-btn"
                                aria-label={t('shoppingCart.decreaseQuantity', { name: getTranslatedProductName(item.name) })}
                                title={t('shoppingCart.decreaseQuantity', { name: getTranslatedProductName(item.name) })}
                              >
                                <i className="fas fa-minus"></i>
                              </button>
                              <span className="quantity">{item.quantity}</span>
                              <button
                                onClick={() => handleIncreaseQuantity(item, stockLimit)}
                                className="quantity-btn"
                                aria-label={t('shoppingCart.increaseQuantity', { name: getTranslatedProductName(item.name) })}
                                title={t('shoppingCart.increaseQuantity', { name: getTranslatedProductName(item.name) })}
                              >
                                <i className="fas fa-plus"></i>
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromCart(item)}
                              className="remove-btn"
                              aria-label={t('shoppingCart.removeItem', { name: getTranslatedProductName(item.name) })}
                              title={t('shoppingCart.removeItem', { name: getTranslatedProductName(item.name) })}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                  
                  <div className="cart-summary">
                    <div className="total-row">
                      <span>{t('shoppingCart.subtotal')}:</span>
                      <span className="total-amount">{formatPrice(cartTotal)}</span>
                    </div>
                    <motion.button
                      className="checkout-btn"
                      onClick={handleCheckout}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <i className="fas fa-shield-alt"></i>
                      {t('shoppingCart.proceedToCheckout')}
                    </motion.button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShoppingCart;
